import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCartItems, selectCartTotal, clearCart, fetchCart, addToCart } from '../cart/cartSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ShippingCalculator from '../../components/shipping/ShippingCalculator';
import DeliveryMethodSelector from '../../components/checkout/DeliveryMethodSelector';
import CustomerFacingSubtotal from '../../components/checkout/CustomerFacingSubtotal';
import {
  calculateMerchantTaxAmount,
  calculateMerchantTaxAmountToAdd,
  getQuoteTaxInfo
} from '../../utils/merchantTax';
// CheckoutProtection removed - checkout handles auth internally based on merchant settings
// import CheckoutProtection from '../../components/CheckoutProtection';
import EmailDetection from '../../components/EmailDetection';
import axios from 'axios';
import { apiFetch } from '../../utils/api';
import { getCustomerBaseUrl } from '../../utils/customerBase';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { getPlatformDomainName } from '../../config/platformConfig';
import { useGA4 } from '../../contexts/GA4Context';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';

function productRequiresDelivery(product = {}) {
  const type = String(product.productType || product.type || '').toUpperCase();
  if (!type) return true;
  return type === 'PHYSICAL';
}

function CheckoutContent() {
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  // Get store from Redux state (populated by StorefrontLayout, works for custom domains)
  const reduxStore = useSelector(state => state.store?.current);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get currency formatting from storefront context
  const { formatPrice, displayCurrency, storeCurrency } = useStorefrontCurrency();

  // Currency formatter helper that uses the context
  const currency = {
    format: (amount) => formatPrice(amount)
  };

  // Helper function to get store slug from platform subdomain only
  // This should NOT return custom domain hostnames as slugs
  const getStoreSlugFromSubdomain = () => {
    const hostname = window.location.hostname;
    const platformDomain = getPlatformDomainName();
    const platformDomainWithoutPort = platformDomain.split(':')[0];

    // Only extract slug from platform subdomains (e.g., storeslug.staging.userevstar.com)
    // NOT from custom domains (e.g., mystore.kwikahost.com)
    if (hostname.endsWith(platformDomainWithoutPort)) {
      const parts = hostname.split('.');
      // If it's like "storeslug.staging.userevstar.com", return the first part
      if (parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'my' && parts[0] !== 'admin') {
        return parts[0];
      }
    }
    return '';
  };

  // Get store slug from location state, query, URL path (/s/:slug), subdomain, or Redux state
  // Redux state is critical for custom domains where slug can't be extracted from URL
  const storeSlug = location.state?.storeSlug
    || new URLSearchParams(location.search).get('store')
    || (window.location.pathname.match(/\/s\/([^/]+)/)?.[1])
    || getStoreSlugFromSubdomain()
    || reduxStore?.slug  // Fallback to Redux state for custom domains
    || '';

  console.log('CheckoutNew: Store slug extraction debug:', {
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    fromState: location.state?.storeSlug,
    fromQuery: new URLSearchParams(location.search).get('store'),
    fromPath: window.location.pathname.match(/\/s\/([^/]+)/)?.[1],
    fromSubdomain: getStoreSlugFromSubdomain(),
    fromRedux: reduxStore?.slug,
    finalStoreSlug: storeSlug
  });

  // Get customer from CustomerAuthContext
  const { customer, isAuthenticated: isCustomerLoggedIn, loading: customerAuthLoading, validateSession } = useCustomerAuth();

  // GA4 tracking
  const { trackPurchase, trackAddPaymentInfo, trackAddShippingInfo } = useGA4();

  // Debug customer auth state
  console.log('=== CheckoutNew Customer Auth Debug ===');
  console.log('Customer:', customer);
  console.log('Is Customer Logged In:', isCustomerLoggedIn);
  console.log('Customer Addresses:', customer?.addresses);
  console.log('Customer Auth Loading:', customerAuthLoading);
  console.log('URL search params:', window.location.search);
  console.log('localStorage customerSessionId:', localStorage.getItem('customerSessionId'));
  console.log('======================================');

  // Store settings state - initialize from Redux if available (for custom domains)
  const [storeSettings, setStoreSettings] = useState(reduxStore || null);
  const [loadingStoreSettings, setLoadingStoreSettings] = useState(!reduxStore);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Payment gateways state
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [paymentCredentials, setPaymentCredentials] = useState({});
  const [loadingGateways, setLoadingGateways] = useState(true);
  const [codAvailable, setCodAvailable] = useState(false);

  // COD is a merchant entitlement, so the public checkout receives only a
  // boolean capability. The checkout API repeats this check before creating
  // an order; this request is solely for a truthful payment-method UI.
  useEffect(() => {
    if (!storeSlug) return;
    let active = true;
    axios.get(`/api/cod/store/${encodeURIComponent(storeSlug)}`)
      .then((response) => { if (active) setCodAvailable(response.data?.available === true); })
      .catch(() => { if (active) setCodAvailable(false); });
    return () => { active = false; };
  }, [storeSlug]);

  // Update storeSettings when Redux store loads (for custom domains)
  useEffect(() => {
    if (reduxStore && !storeSettings) {
      console.log('CheckoutNew: Using store settings from Redux state:', reduxStore);
      setStoreSettings(reduxStore);
      setLoadingStoreSettings(false);
    }
  }, [reduxStore, storeSettings]);

  // Handle direct product checkout from QR code (?product=xxx&qty=1&variations=...)
  const [directProductLoading, setDirectProductLoading] = useState(false);
  useEffect(() => {
    const handleDirectProductCheckout = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const productId = urlParams.get('product');
      const qty = parseInt(urlParams.get('qty')) || 1;
      const variationsParam = urlParams.get('variations');
      const priceParam = urlParams.get('price');

      if (!productId || !storeSlug) return;

      // Check if product is already in cart (with same variations if applicable)
      const existingItem = items.find(item => {
        const sameProduct = item.product?.id === productId || item.product?._id === productId || item.productId === productId;
        if (!sameProduct) return false;
        // If variations provided, check if they match
        if (variationsParam) {
          try {
            const urlVariations = JSON.parse(variationsParam);
            return JSON.stringify(item.variations) === JSON.stringify(urlVariations);
          } catch {
            return sameProduct;
          }
        }
        return sameProduct;
      });

      if (existingItem) {
        console.log('[Checkout] Product already in cart, skipping add');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      try {
        setDirectProductLoading(true);
        console.log('[Checkout] Fetching product for direct checkout:', productId);

        // Fetch product details from products API
        const response = await axios.get(`/api/products/${productId}`);
        const product = response.data.product || response.data;

        if (product && product.id) {
          // Check if product has variations
          const hasVariations = product.variations && product.variations.length > 0;

          // If product has variations but no variations provided in URL, redirect to product page
          if (hasVariations && !variationsParam) {
            console.log('[Checkout] Product has variations but none selected, redirecting to product page');
            const productPageUrl = `/product/${product.slug || product.id}?from_qr=1`;
            navigate(productPageUrl, { replace: true });
            return;
          }

          // Parse variations from URL if provided
          let selectedVariations = null;
          if (variationsParam) {
            try {
              selectedVariations = JSON.parse(variationsParam);
              console.log('[Checkout] Using variations from QR:', selectedVariations);
            } catch (e) {
              console.error('[Checkout] Failed to parse variations:', e);
            }
          }

          // Use price from URL if provided (for variation-based pricing)
          const finalPrice = priceParam ? parseFloat(priceParam) : product.price;

          console.log('[Checkout] Adding product to cart:', product.name, { selectedVariations, finalPrice });

          // Add to cart with variations
          dispatch(addToCart({
            product: { ...product, price: finalPrice },
            qty,
            variations: selectedVariations
          }));
        } else {
          console.error('[Checkout] Product not found or invalid response');
        }

        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('[Checkout] Error fetching product for direct checkout:', error);
        // Clean up URL even on error
        window.history.replaceState({}, document.title, window.location.pathname);
      } finally {
        setDirectProductLoading(false);
      }
    };

    handleDirectProductCheckout();
  }, [storeSlug, dispatch, items, navigate]);

  // Fetch store settings to check requireAccountRegistration
  // This is a fallback if Redux doesn't have the store data
  const fetchStoreSettings = useCallback(async () => {
    // Skip if we already have store settings from Redux
    if (storeSettings) {
      console.log('CheckoutNew: Store settings already available from Redux');
      setLoadingStoreSettings(false);
      return;
    }

    if (!storeSlug) {
      console.log('CheckoutNew: No store slug available yet, waiting...');
      return;
    }

    try {
      setLoadingStoreSettings(true);
      const response = await axios.get(`/api/stores/${storeSlug}`);

      if (response.data && response.data.store) {
        // Store the nested store object directly
        setStoreSettings(response.data.store);
        console.log('CheckoutNew: Store settings loaded from API:', response.data.store);
      }
    } catch (error) {
      console.error('Error fetching store settings:', error);
    } finally {
      setLoadingStoreSettings(false);
    }
  }, [storeSlug, storeSettings]);

  // Fetch enabled payment gateways for this store
  const fetchPaymentGateways = useCallback(async () => {
    if (!storeSlug) {
      setLoadingGateways(false);
      return;
    }

    try {
      setLoadingGateways(true);
      const response = await axios.get(`/api/merchant/payment-gateways/store/${storeSlug}/gateways`);

      // Handle different response formats (status or success field)
      // ResponseHelper returns { status: 'success', data: [...] }
      let gateways = [];

      if (Array.isArray(response.data?.data)) {
        gateways = response.data.data;
      } else if (Array.isArray(response.data?.gateways)) {
        gateways = response.data.gateways;
      } else if (Array.isArray(response.data)) {
        // Direct array response
        gateways = response.data;
      }

      const isSuccess = response.data?.status === 'success' || response.data?.success === true || gateways.length > 0;

      console.log('[CHECKOUT DEBUG] Raw gateways from API:', gateways);
      console.log('[CHECKOUT DEBUG] Gateways count:', gateways.length);

      if (isSuccess && gateways.length > 0) {
        // Ensure each gateway has required fields
        const validGateways = gateways.filter(g => g && g.id && g.name).map(g => ({
          id: g.id,
          name: g.name,
          description: g.description || '',
          icon: g.icon || '💳',
          color: g.color || '#00D4AA',
          enabled: g.enabled !== false
        }));

        console.log('[CHECKOUT DEBUG] Valid gateways after filter:', validGateways);
        console.log('[CHECKOUT DEBUG] Valid gateways count:', validGateways.length);

        if (validGateways.length > 0) {
          setPaymentGateways(validGateways);
          setPaymentMethod(validGateways[0].id);
          console.log('[CHECKOUT DEBUG] Setting payment gateways state:', validGateways);
        } else {
          throw new Error('No valid payment gateways');
        }
      } else {
        // A merchant can deliberately offer only Bank Transfer or COD. Do not
        // invent a Paystack option when the public gateway endpoint is empty.
        setPaymentGateways([]);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      setPaymentGateways([]);
    } finally {
      setLoadingGateways(false);
    }
  }, [storeSlug]);

  // Fetch payment gateway credentials
  const fetchPaymentCredentials = useCallback(async () => {
    if (!storeSlug) return;

    try {
      const response = await axios.get(`/api/merchant/payment-gateways/store/${storeSlug}/credentials`);
      
      if (response.data.status === 'success') {
        setPaymentCredentials(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payment credentials:', error);
    }
  }, [storeSlug]);
  
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Nigeria'
  });
  
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Nigeria'
  });
  
  const [useBillingForShipping, setUseBillingForShipping] = useState(false);
  const [hasPhysicalProducts, setHasPhysicalProducts] = useState(false);
  const [shippingCalculation, setShippingCalculation] = useState(null);
  const [calculatedTotal, setCalculatedTotal] = useState(total);
  const [serviceChargeInfo, setServiceChargeInfo] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);

  // Delivery method state (for Chowdeck integration)
  const [deliveryMethod, setDeliveryMethod] = useState('STATIC');
  const [chowdeckFeeId, setChowdeckFeeId] = useState(null);
  const [chowdeckQuoteToken, setChowdeckQuoteToken] = useState(null);
  const [chowdeckDeliveryFee, setChowdeckDeliveryFee] = useState(null);
  
  // Calculate current state for shipping
  const currentState = useBillingForShipping ? customerInfo.state : shippingInfo.state;
  
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const getEffectiveShippingFee = useCallback((shippingData, currentDeliveryMethod = deliveryMethod) => {
    if (currentDeliveryMethod === 'CHOWDECK') {
      return chowdeckDeliveryFee || 0;
    }
    return shippingData?.finalShippingCost ?? shippingData?.shippingCost ?? 0;
  }, [deliveryMethod, chowdeckDeliveryFee]);

  // Coupon/discount state
  const [couponCode, setCouponCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // Email detection state
  const [showEmailDetection, setShowEmailDetection] = useState(false);
  const [detectedEmail, setDetectedEmail] = useState('');
  const emailTimeoutRef = useRef(null);

  // Email detection handlers
  const handleEmailChange = (e) => {
    const email = e.target.value;

    console.log('🔵 handleEmailChange triggered with email:', email);
    console.log('🔵 customerAuthLoading:', customerAuthLoading);
    console.log('🔵 isCustomerLoggedIn:', isCustomerLoggedIn);
    console.log('🔵 customer?.email:', customer?.email);
    console.log('🔵 requireAccountRegistration:', storeSettings?.requireAccountRegistration);

    // CRITICAL FIX: Don't allow email changes while auth is loading
    // This prevents race conditions where user types email before customer data loads
    if (customerAuthLoading) {
      console.log('❌ CheckoutNew: Email change blocked - auth still loading');
      return;
    }

    // Prevent email change if customer is logged in
    if (isCustomerLoggedIn && customer?.email) {
      console.log('❌ CheckoutNew: Email change blocked - customer is logged in');
      return;
    }

    console.log('✅ Setting customerInfo email to:', email);
    setCustomerInfo(prev => ({ ...prev, email }));

    // Clear previous timeout
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    // CRITICAL FIX: Don't trigger email detection if requireAccountRegistration is true
    // Guest customers can't checkout anyway, so no point checking if email exists
    // Only trigger for stores that allow guest checkout
    if (email && email.includes('@') && !isCustomerLoggedIn && !customerAuthLoading && !storeSettings?.requireAccountRegistration) {
      console.log('🔵 Setting email detection timeout');
      emailTimeoutRef.current = setTimeout(() => {
        console.log('🔵 Email detection timeout fired for:', email);
        setDetectedEmail(email);
        setShowEmailDetection(true);
      }, 1000);
    } else {
      console.log('⏭️ Skipping email detection');
    }
  };

  const handleEmailExists = (email) => {
    setShowEmailDetection(false);
    // Redirect to login with email pre-filled
    const currentUrl = window.location.href;
    const redirectUrl = encodeURIComponent(currentUrl);
    window.location.href = `${getCustomerBaseUrl()}/login?redirect=${redirectUrl}&email=${encodeURIComponent(email)}`;
  };

  const handleEmailNew = (email) => {
    setShowEmailDetection(false);

    // If requireAccountRegistration is enabled, don't allow new email - force registration
    if (storeSettings?.requireAccountRegistration) {
      // Redirect to registration page
      const currentUrl = window.location.href;
      const redirectUrl = encodeURIComponent(currentUrl);
      window.location.href = `${getCustomerBaseUrl()}/register?redirect=${redirectUrl}&email=${encodeURIComponent(email)}`;
      return;
    }

    // Continue with guest checkout
    setCustomerInfo(prev => ({ ...prev, email }));
  };

  const handleEmailDetectionCancel = () => {
    // If requireAccountRegistration is enabled, don't allow cancel
    if (storeSettings?.requireAccountRegistration) {
      return;
    }

    setShowEmailDetection(false);
    setDetectedEmail('');
  };

  // Track checkout abandonment when customer email is captured
  const checkoutTrackingRef = useRef(false);
  useEffect(() => {
    const email = customerInfo.email;
    if (!email || !email.includes('@') || !items.length || !storeSlug || checkoutTrackingRef.current) return;

    // Only track once per checkout session with this email
    checkoutTrackingRef.current = true;

    const trackCheckout = async () => {
      try {
        let sessionId = sessionStorage.getItem('cart_session_id');
        if (!sessionId) {
          sessionId = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
          sessionStorage.setItem('cart_session_id', sessionId);
        }

        await apiFetch('/abandoned-carts/track-checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            items: items.map(item => ({
              productId: item.id || item._id,
              quantity: item.qty || item.quantity || 1,
              price: item.price,
              name: item.name,
              image: item.images?.[0] || item.image || ''
            })),
            storeSlug,
            subtotal: total,
            currency: 'NGN',
            contact: {
              email,
              phone: customerInfo.phone || '',
              name: `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim() || ''
            }
          })
        });
        console.log('[AbandonedCheckout] Tracked checkout entry');
      } catch (e) {
        console.error('[AbandonedCheckout] Track error:', e.message);
      }
    };

    trackCheckout();
  }, [customerInfo.email, items, storeSlug, total, customerInfo.phone, customerInfo.firstName, customerInfo.lastName]);

  // Service charge calculation function
  const calculateServiceCharge = useCallback(async (subtotal, overrideShippingCost = null, overrideDiscountAmount = null) => {
    const getTaxFallback = async (discountAmt) => {
      if (!storeSlug) return null;

      try {
        const taxResponse = await apiFetch(`/merchant/payment-gateways/store/${encodeURIComponent(storeSlug)}/tax-settings`);
        if (!taxResponse.ok) return null;
        const payload = await taxResponse.json();
        const settings = payload.data || payload;
        if (!settings?.enabled || Number(settings.rate || 0) <= 0) return null;

        const taxableSubtotal = Math.max(0, Number(subtotal || 0) - Number(discountAmt || 0));
        const taxAmount = calculateMerchantTaxAmount(taxableSubtotal, settings);
        const taxAmountToAdd = calculateMerchantTaxAmountToAdd(taxableSubtotal, settings);

        return {
          taxEnabled: true,
          taxAmount,
          taxTotal: taxAmount,
          taxRate: Number(settings.rate || 0),
          taxName: settings.name || 'Tax',
          taxInclusive: settings.inclusive === true,
          taxSettings: {
            name: settings.name || 'Tax',
            rate: Number(settings.rate || 0),
            inclusive: settings.inclusive === true
          },
          taxAmountToAdd
        };
      } catch (err) {
        return null;
      }
    };

    try {
      const shippingCost = overrideShippingCost !== null ? overrideShippingCost : getEffectiveShippingFee(shippingCalculation, 'STATIC');
      const discountAmt = overrideDiscountAmount !== null ? overrideDiscountAmount : (appliedDiscount?.discountAmount || 0);

      const response = await apiFetch('/service-charge/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subtotal: subtotal,
          shippingFee: shippingCost,
          discountAmount: discountAmt,
          storeSlug: storeSlug
        })
      });

      if (response.ok) {
        const data = await response.json();
        const quoteTax = getQuoteTaxInfo(data);
        if (!quoteTax.enabled || quoteTax.amount <= 0) {
          const fallbackTax = await getTaxFallback(discountAmt);
          if (fallbackTax) {
            const adjustedTotal = Number(data.total || 0) + fallbackTax.taxAmountToAdd;
            setServiceChargeInfo({ ...data, ...fallbackTax });
            setCalculatedTotal(adjustedTotal);
            return;
          }
        }
        setServiceChargeInfo(data);
        setCalculatedTotal(data.total);
      } else {
        const fallbackTax = await getTaxFallback(discountAmt);
        setServiceChargeInfo(fallbackTax || null);
        setCalculatedTotal(subtotal - discountAmt + shippingCost + (fallbackTax?.taxAmountToAdd || 0));
      }
    } catch (error) {
      console.error('Failed to calculate service charge:', error);
      const discountAmt = overrideDiscountAmount !== null ? overrideDiscountAmount : (appliedDiscount?.discountAmount || 0);
      const fallbackTax = await getTaxFallback(discountAmt);
      setServiceChargeInfo(fallbackTax || null);
      setCalculatedTotal(subtotal - discountAmt + (overrideShippingCost !== null ? overrideShippingCost : getEffectiveShippingFee(shippingCalculation, 'STATIC')) + (fallbackTax?.taxAmountToAdd || 0));
    }
  }, [storeSlug, shippingCalculation, appliedDiscount?.discountAmount, getEffectiveShippingFee]);

  // Memoized callback for shipping calculation
  const handleShippingCalculated = useCallback((data) => {
    console.log('CheckoutNew: Shipping calculated callback received:', data);
    console.log('CheckoutNew: Setting shippingCalculation state to:', data);
    setShippingCalculation(data);
    setShippingLoading(false); // Mark shipping as loaded

    // GA4: Track add_shipping_info event
    const effectiveShippingFee = getEffectiveShippingFee(data, 'STATIC');

    if (data && data.shippingCost !== undefined) {
      trackAddShippingInfo({
        currency: storeCurrency || 'NGN',
        total: total + effectiveShippingFee,
        shippingTier: data.method || 'Standard',
        items: items.map(({ product, qty }) => ({
          productId: product._id || product.id,
          name: product.name,
          price: product.price,
          quantity: qty
        }))
      });
    }

    // Recalculate service charge with new shipping cost
    console.log('CheckoutNew: Recalculating service charge with shipping:', effectiveShippingFee);
    calculateServiceCharge(total, effectiveShippingFee);

    console.log('CheckoutNew: shippingCalculation state updated');
  }, [total, calculateServiceCharge, trackAddShippingInfo, items, getEffectiveShippingFee]);

  // Handle delivery method change (Chowdeck integration)
  const handleDeliveryMethodChange = useCallback((data) => {
    console.log('CheckoutNew: Delivery method changed:', data);
    setDeliveryMethod(data.method);

    if (data.method === 'CHOWDECK') {
      setChowdeckFeeId(data.feeId);
      setChowdeckQuoteToken(data.quoteToken);
      setChowdeckDeliveryFee(data.fee);
      // Recalculate with Chowdeck fee
      calculateServiceCharge(total, data.fee);
    } else {
      setChowdeckFeeId(null);
      setChowdeckQuoteToken(null);
      setChowdeckDeliveryFee(null);
      // Recalculate with standard shipping
      calculateServiceCharge(total, getEffectiveShippingFee(shippingCalculation, 'STATIC'));
    }
  }, [total, calculateServiceCharge, shippingCalculation, getEffectiveShippingFee]);

  // Calculate service charge on component load and when shipping changes
  useEffect(() => {
    if (total > 0 && storeSlug) {
      calculateServiceCharge(total);
    } else if (total > 0) {
      setCalculatedTotal(total + getEffectiveShippingFee(shippingCalculation, 'STATIC'));
    }

    fetchStoreSettings();
    fetchPaymentGateways();
    fetchPaymentCredentials();
  }, [total, calculateServiceCharge, storeSlug, shippingCalculation, fetchStoreSettings, fetchPaymentGateways, fetchPaymentCredentials, getEffectiveShippingFee]);

  // Use the calculated total from service charge calculation
  // Note: If discount is applied, calculatedTotal already includes the discount
  // (set from backend's finalAmount which has discount pre-applied)
  const discountAmount = appliedDiscount?.discountAmount || 0;
  // COD is included in the merchant's plan. The backend is authoritative,
  // but the checkout must not show a customer transaction fee before the
  // order is submitted. Tax and delivery remain part of the cash due.
  const customerTransactionFee = serviceChargeInfo
    && String(serviceChargeInfo.whoPays || '').toLowerCase() === 'customer'
    ? Number(serviceChargeInfo.serviceCharge || 0)
    : 0;
  const finalTotal = paymentMethod === 'cod'
    ? Math.max(0, calculatedTotal - customerTransactionFee)
    : calculatedTotal;
  const resolvedShippingFee = getEffectiveShippingFee(shippingCalculation);
  const platformFeeQuote = serviceChargeInfo?.breakdown?.platformFeeQuote || serviceChargeInfo?.platformFeeQuote || null;
  const quoteTax = getQuoteTaxInfo(serviceChargeInfo || {});
  const sanitizeGatewayKey = (key) => key?.trim().replace(/[\r\n\t]/g, '');
  const manualTransferAvailable = storeSettings?.manualTransferEnabled === true
    && String(storeSettings?.currency || storeCurrency || 'NGN').toUpperCase() === 'NGN';
  const bankTransferIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10h18M5 10V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4" />
      <path d="M4 10v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M9 16h2M14 16h1" />
    </svg>
  );
  const codIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M6.5 10.5v3M17.5 10.5v3" />
    </svg>
  );
  // COD requires a physical delivery event. Any digital/service line item
  // removes COD from the picker; the backend enforces the same rule.
  const cartIsAllPhysical = Array.isArray(items) && items.length > 0
    && items.every((item) => productRequiresDelivery(item.product));
  const checkoutPaymentMethods = useMemo(() => [
    ...paymentGateways,
    ...(manualTransferAvailable ? [{
      id: 'BANK_TRANSFER',
      name: 'Bank transfer',
      description: 'Transfer to the merchant account',
      iconElement: bankTransferIcon,
      color: '#2563eb'
    }] : []),
    ...(codAvailable && cartIsAllPhysical ? [{
      id: 'cod',
      name: 'Pay on delivery',
      description: 'Pay when your order is delivered',
      iconElement: codIcon,
      color: '#087F6A'
    }] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [paymentGateways, manualTransferAvailable, codAvailable, cartIsAllPhysical]);

  // Keep a real available method selected as merchant settings load/change.
  useEffect(() => {
    if (!loadingGateways && checkoutPaymentMethods.length > 0
      && !checkoutPaymentMethods.some((method) => method.id === paymentMethod)) {
      setPaymentMethod(checkoutPaymentMethods[0].id);
    }
  }, [loadingGateways, paymentMethod, checkoutPaymentMethods]);

  // Check authentication requirement when store settings and auth state are loaded
  useEffect(() => {
    // Wait for both store settings and customer auth context to be loaded
    if (loadingStoreSettings) {
      console.log('CheckoutNew: Still loading store settings...');
      return;
    }

    console.log('CheckoutNew: Store settings loaded:', storeSettings);
    console.log('CheckoutNew: requireAccountRegistration:', storeSettings?.requireAccountRegistration);
    console.log('CheckoutNew: isCustomerLoggedIn:', isCustomerLoggedIn);
    console.log('CheckoutNew: customerAuthLoading:', customerAuthLoading);

    // Don't redirect while authentication is still loading
    // CustomerAuthContext handles sessionId from URL automatically
    if (customerAuthLoading) {
      console.log('CheckoutNew: Customer auth still loading (may be validating sessionId), waiting...');
      return;
    }

    // Check if we have sessionId in localStorage (indicating we returned from login)
    const hasSessionIdInStorage = !!localStorage.getItem('customerSessionId');
    const hasRedirectedToLogin = sessionStorage.getItem('checkoutRedirectedToLogin') === 'true';

    console.log('CheckoutNew: Session check:', {
      hasSessionIdInStorage,
      hasRedirectedToLogin,
      isCustomerLoggedIn
    });

    // CRITICAL FIX: If redirect flag is set but we don't have sessionId and not logged in
    // This means login failed or sessionId wasn't passed - clear the flag and allow guest checkout
    if (hasRedirectedToLogin && !hasSessionIdInStorage && !isCustomerLoggedIn) {
      console.warn('⚠️ CheckoutNew: Redirect flag set but no sessionId - clearing flag to prevent loop');
      sessionStorage.removeItem('checkoutRedirectedToLogin');
      // Don't redirect again, let them proceed as guest or manually login
      setCheckingAuth(false);
      return;
    }

    // CRITICAL: If we have sessionId in storage but user not logged in yet
    // This means CustomerAuthContext is still validating the session
    // We must wait for validation to complete before making any redirect decisions
    if (hasSessionIdInStorage && !isCustomerLoggedIn) {
      console.log('🔄 CheckoutNew: SessionId exists but not authenticated yet - waiting for validation');
      setCheckingAuth(true);
      return;
    }

    // If we have sessionId and user IS logged in, validation was successful
    if (hasSessionIdInStorage && isCustomerLoggedIn) {
      console.log('✅ CheckoutNew: SessionId validation successful - user authenticated!');
      sessionStorage.removeItem('checkoutRedirectedToLogin'); // Clear redirect flag
      setCheckingAuth(false);
      return;
    }

    // If registration is required and customer is not logged in, redirect to login
    // BUT only redirect ONCE (tracked in sessionStorage)
    if (storeSettings?.requireAccountRegistration && !isCustomerLoggedIn && !hasRedirectedToLogin) {
      console.log('CheckoutNew: Registration required but customer not logged in - redirecting to login');

      // Mark that we've redirected (persists across page reloads)
      sessionStorage.setItem('checkoutRedirectedToLogin', 'true');

      const currentUrl = window.location.href;
      const redirectUrl = encodeURIComponent(currentUrl);
      window.location.href = `${getCustomerBaseUrl()}/login?redirect=${redirectUrl}&store=${storeSlug}`;
      return;
    }

    // If user IS logged in, clear the redirect flag and mark auth as complete
    if (isCustomerLoggedIn) {
      console.log('✅ CheckoutNew: Customer is logged in - auth check complete');
      sessionStorage.removeItem('checkoutRedirectedToLogin');
    }

    // Auth check complete
    setCheckingAuth(false);
  }, [storeSettings, loadingStoreSettings, isCustomerLoggedIn, customerAuthLoading, storeSlug]);

  // Track if we've already prefilled to prevent multiple updates
  const hasPrefilledRef = useRef(false);

  // Pre-fill customer information when customer is loaded
  useEffect(() => {
    console.log('🔵 CheckoutNew Pre-fill triggered');
    console.log('🔵 CheckoutNew Pre-fill: Auth loading:', customerAuthLoading);
    console.log('🔵 CheckoutNew Pre-fill: Customer data:', customer);
    console.log('🔵 CheckoutNew Pre-fill: Customer email:', customer?.email);
    console.log('🔵 CheckoutNew Pre-fill: Customer addresses count:', customer?.addresses?.length);
    console.log('🔵 CheckoutNew Pre-fill: Is customer logged in:', isCustomerLoggedIn);
    console.log('🔵 CheckoutNew Pre-fill: Has already prefilled:', hasPrefilledRef.current);
    console.log('🔵 CheckoutNew Pre-fill: Current customerInfo.email:', customerInfo.email);

    // CRITICAL FIX: Wait for auth to finish loading before pre-filling
    // This ensures customer data is fully loaded
    if (customerAuthLoading) {
      console.log('🔵 CheckoutNew Pre-fill: Waiting for auth to complete...');
      return;
    }

    // Prevent multiple prefills - only prefill once when customer data first loads
    if (hasPrefilledRef.current) {
      console.log('🔵 CheckoutNew Pre-fill: Already prefilled, skipping...');
      return;
    }

    if (customer && isCustomerLoggedIn && customer.email) {
      const billingAddress = customer.addresses?.find(addr => addr.type === 'billing') || customer.addresses?.[0];
      console.log('🔵 CheckoutNew Pre-fill: Billing address found:', billingAddress);

      const customerData = {
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: billingAddress?.address1 || '',
        city: billingAddress?.city || '',
        state: billingAddress?.state || '',
        zipCode: billingAddress?.postalCode || '',
        country: billingAddress?.country || 'Nigeria'
      };

      console.log('✅ CheckoutNew Pre-fill: Setting customer info:', customerData);
      setCustomerInfo(customerData);

      // Also set shipping info to same as billing initially
      setShippingInfo(customerData);
      setUseBillingForShipping(true);

      // Mark as prefilled
      hasPrefilledRef.current = true;
      console.log('✅ CheckoutNew Pre-fill: Prefill complete, marked as done');
    } else {
      console.log('⚠️ CheckoutNew Pre-fill: Cannot prefill. Reasons:', {
        hasCustomer: !!customer,
        isLoggedIn: isCustomerLoggedIn,
        hasEmail: !!customer?.email
      });
    }
  }, [customer, isCustomerLoggedIn, customerAuthLoading]);

  // Check if cart has physical products
  useEffect(() => {
    const hasPhysical = items.some((item) => productRequiresDelivery(item.product));
    setHasPhysicalProducts(hasPhysical);
  }, [items]);

  // Service charge removed - no longer charging service fees

  // Set default shipping for digital products
  useEffect(() => {
    if (!hasPhysicalProducts) {
      setShippingCalculation({
        finalShippingCost: 0,
        shippingCost: 0,
        method: 'Digital Delivery',
        estimatedDays: 'Instant'
      });
      setShippingLoading(false);
    }
  }, [hasPhysicalProducts]);

  // Track when shipping calculation is needed (address/state changes for physical products)
  useEffect(() => {
    if (hasPhysicalProducts && currentState) {
      // Mark shipping as loading when state changes
      setShippingLoading(true);
    }
  }, [hasPhysicalProducts, currentState]);

  // ShippingCalculator will handle recalculation when address changes

  // Note: calculateServiceCharge useEffect (lines 216-226) handles total calculation
  // including shipping and service charges, so we don't need a separate useEffect here

  // Fetch cart on mount and when customer changes
  useEffect(() => {
    // Wait for customer auth to finish loading before fetching cart
    if (customerAuthLoading) {
      console.log('CheckoutNew: Waiting for customer auth to load before fetching cart');
      return;
    }

    console.log('CheckoutNew: Fetching cart for customer:', customer?._id || 'guest');
    dispatch(fetchCart(customer?._id));
  }, [dispatch, customer?._id, customerAuthLoading]);

  // Cart persistence is now handled by the cart ID system in axiosConfig
  // The cart ID persists in localStorage and is sent with every request
  // No manual cart restoration needed!

  // Auto-apply discount code from URL or sessionStorage
  const autoApplyDiscountRef = useRef(false);

  useEffect(() => {
    // Only run once on mount
    if (autoApplyDiscountRef.current || appliedDiscount) return;

    // First check URL params
    const urlParams = new URLSearchParams(location.search);
    let discountFromUrl = urlParams.get('discount');

    // Then check sessionStorage
    const storageKey = `pendingDiscount_${storeSlug}`;
    const discountFromStorage = sessionStorage.getItem(storageKey);

    const discountToApply = discountFromUrl || discountFromStorage;

    if (discountToApply) {
      autoApplyDiscountRef.current = true;
      console.log(`[Checkout] Auto-applying discount code: ${discountToApply}`);
      setCouponCode(discountToApply);

      // Auto-trigger apply button click after a short delay
      setTimeout(() => {
        const applyButton = document.querySelector('[data-apply-coupon]');
        if (applyButton) {
          applyButton.click();
        }

        // Remove from sessionStorage
        if (discountFromStorage) {
          sessionStorage.removeItem(storageKey);
        }
        // Clean URL
        if (discountFromUrl) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('discount');
          window.history.replaceState({}, '', newUrl.toString());
        }
      }, 1500);
    }
  }, [storeSlug, appliedDiscount, location.search]);

  const validateForm = () => {
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.email || !customerInfo.phone) {
      setError('Please fill in all required customer information');
      return false;
    }
    
    if (hasPhysicalProducts && !useBillingForShipping) {
      if (!shippingInfo.firstName || !shippingInfo.lastName || !shippingInfo.address || !shippingInfo.city || !shippingInfo.state) {
        setError('Please fill in all required shipping information');
        return false;
      }
    }
    
    return true;
  };

  const handlePaymentVerification = async (reference, orderIdToVerify) => {
    try {
      console.log('Verifying payment with reference:', reference);

      const response = await axios.post(
        '/api/customer/checkout/verify-payment',
        {
          reference,
          orderId: orderIdToVerify,
          storeSlug,
          paymentMethod: paymentMethod || 'paystack' // Pass the payment method used
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        console.log('Payment verification successful:', response.data);

        // GA4: Track purchase
        trackPurchase({
          id: orderIdToVerify,
          orderId: orderIdToVerify,
          total: total,
          currency: storeCurrency || 'NGN',
          tax: 0,
          shippingCost: resolvedShippingFee,
          items: items.map(({ product, qty }) => ({
            productId: product._id || product.id,
            name: product.name,
            price: product.price,
            quantity: qty
          }))
        });

        // Clear cart after successful payment
        dispatch(clearCart());

        // Clear deep link attribution token after conversion
        localStorage.removeItem('dl_token');

        // Redirect to order confirmation page (single destination)
        navigate('/order-confirmation', {
          state: { orderId: orderIdToVerify, storeSlug, reference }
        });
      } else {
        throw new Error(response.data.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setError(error.response?.data?.message || 'Payment verification failed');
    }
  };

  // Apply coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      // Get merchant ID from store settings
      const merchantId = storeSettings?.ownerId || storeSettings?.store?.ownerId;

      if (!merchantId) {
        setCouponError('Store information not loaded. Please try again.');
        setCouponLoading(false);
        return;
      }

      // Debug: log the payload being sent
      const payload = {
        code: couponCode.trim(),
        merchantId: merchantId,
        orderData: {
          subtotal: total,
          shippingFee: resolvedShippingFee, // Include shipping fee
          items: items.map(item => ({
            productId: item.product._id || item.product.id,
            quantity: item.qty,
            price: item.product.price
          }))
        },
        customerId: customer?.id,
        storeSlug: storeSlug
      };

      console.log('Coupon validation payload:', payload);
      console.log('storeSettings:', storeSettings);

      const response = await axios.post(
        '/api/discounts/validate',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

      if (response.data.success) {
        const discountAmt = response.data.discountAmount;
        setAppliedDiscount({
          code: couponCode.trim(),
          discountAmount: discountAmt,
          finalAmount: response.data.finalAmount,
          serviceCharge: response.data.serviceCharge, // Store updated service charge
          details: response.data.discount
        });

        // Recalculate service charge with discount to get correct tax and totals
        calculateServiceCharge(total, getEffectiveShippingFee(shippingCalculation, 'STATIC'), discountAmt);

        setCouponError('');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCouponError(error.response?.data?.error || 'Invalid coupon code');
      setAppliedDiscount(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedDiscount(null);
    setCouponError('');
    // Recalculate service charge with original subtotal (without discount)
    calculateServiceCharge(total, getEffectiveShippingFee(shippingCalculation, 'STATIC'), 0);
  };

  const handlePayment = async () => {
    console.log('CheckoutNew: Pay button clicked');
    console.log('CheckoutNew: validateForm result:', validateForm());

    if (!validateForm()) {
      console.log('CheckoutNew: Form validation failed');
      return;
    }

    // GA4: Track add_payment_info event when user clicks pay
    trackAddPaymentInfo({
      currency: storeCurrency || 'NGN',
      total: finalTotal,
      paymentType: paymentMethod || 'paystack',
      items: items.map(({ product, qty }) => ({
        productId: product._id || product.id,
        name: product.name,
        price: product.price,
        quantity: qty
      }))
    });

    // Debug authentication via context only
    console.log('CheckoutNew: Is customer logged in:', isCustomerLoggedIn);

    setLoading(true);
    setError(null);

    try {
      console.log('CheckoutNew: Starting payment process');
      // Create order first
      // Get deep link attribution token if present
      const dlToken = localStorage.getItem('dl_token') || null;

      const orderData = {
        customerInfo: customerInfo,
        shippingInfo: hasPhysicalProducts ? (useBillingForShipping ? customerInfo : shippingInfo) : null,
        useBillingForShipping: hasPhysicalProducts ? useBillingForShipping : false,
        items: items.map(({ product, qty }) => ({
          productId: product._id || product.id,
          name: product.name,
          price: product.price,
          qty: qty,
          quantity: qty // Add both qty and quantity for compatibility
        })),
        subtotal: total,
        total: finalTotal, // Use finalTotal which includes discount
        shippingFee: resolvedShippingFee,
        paymentMethod: paymentMethod,
        storeSlug: storeSlug,
        // Chowdeck delivery method (if selected)
        deliveryMethod: deliveryMethod,
        chowdeckFeeId: deliveryMethod === 'CHOWDECK' ? chowdeckFeeId : null,
        chowdeckQuoteToken: deliveryMethod === 'CHOWDECK' ? chowdeckQuoteToken : null,
        // Deep link attribution
        dl_token: dlToken
      };

      console.log('=== CHECKOUT DEBUG: Frontend Order Data ===');
      console.log('Cart total (displayed):', total);
      console.log('Final total (with service charge):', finalTotal);
      console.log('Shipping fee:', resolvedShippingFee);
      console.log('Items being sent:');
      orderData.items.forEach((item, idx) => {
        console.log(`  Item ${idx + 1}: ${item.name} - Price: ${item.price} x Qty: ${item.qty} = ${item.price * item.qty}`);
      });
      const calculatedSubtotalFromItems = orderData.items.reduce((sum, item) => sum + (item.price * item.qty), 0);
      console.log('Subtotal calculated from items array:', calculatedSubtotalFromItems);
      console.log('Cart total (from state):', total);
      console.log('Difference:', calculatedSubtotalFromItems - total);
      console.log('=========================================');
      console.log('Creating order with data:', orderData);

      // Use customer checkout API if customer is logged in, otherwise use regular order API
      const orderResponse = isCustomerLoggedIn
        ? await axios.post(
            '/api/checkout/checkout',
            {
              orderData,
              storeSlug,
              discountCode: appliedDiscount?.code // Include discount code if applied
            },
            {
              headers: {
                'Content-Type': 'application/json'
              },
              withCredentials: true // Include session cookies
            }
          )
        : await axios.post(
            '/api/checkout/guest-checkout',
            {
              customerInfo,
              orderData,
              storeSlug,
              discountCode: appliedDiscount?.code, // Include discount code if applied
              createAccount: false // For now, don't create account during guest checkout
            },
            {
              withCredentials: true // Include session cookies
            }
          );

      console.log('CheckoutNew: Order initialized (pending payment):', orderResponse.data);

      if (orderResponse.data?.cod) {
        dispatch(clearCart());
        localStorage.removeItem('dl_token');
        navigate('/order-confirmation', {
          state: {
            orderId: orderResponse.data.order?.id,
            storeSlug,
            order: orderResponse.data.order,
            cod: true,
            codDecisionStatus: orderResponse.data.order?.codDecisionStatus,
          },
        });
        return;
      }

      if (paymentMethod === 'BANK_TRANSFER' && orderResponse.data?.virtualTerminal) {
        dispatch(clearCart());
        localStorage.removeItem('dl_token');
        navigate('/order-confirmation', {
          state: {
            orderId: orderResponse.data.order?.id,
            storeSlug,
            order: orderResponse.data.order,
            manualTransfer: {
              ...orderResponse.data.manualTransfer,
              virtualTerminal: orderResponse.data.virtualTerminal,
            },
          },
        });
        return;
      }

      const payment = orderResponse.data?.payment;

      // Check if Flutterwave requires inline checkout (client-side initialization)
      if (payment?.useInlineCheckout && payment?.provider === 'FLUTTERWAVE') {
        console.log('[FLUTTERWAVE] Using inline checkout with data:', payment.paymentData);
        await handleFlutterwaveInlineCheckout(payment, orderResponse.data?.order);
        return;
      }

      // Check if backend returned a payment URL (Paystack flow)
      // Backend returns snake_case (authorization_url) from Paystack API
      const paymentUrl = payment?.authorization_url || payment?.authorizationUrl;
      if (paymentUrl) {
        console.log('Using payment URL from backend:', paymentUrl);
        // Redirect to payment gateway URL
        window.location.href = paymentUrl;
        return;
      }

      // Fallback to old client-side initialization (if no payment URL from backend)
      const created = orderResponse.data?.order ? orderResponse.data.order : orderResponse.data;
      const orderIdToVerify = created?.id || created?._id;

      // Route to appropriate payment handler based on selected method
      if (paymentMethod === 'paystack') {
        await handlePaystackPayment(created);
      } else if (paymentMethod === 'flutterwave') {
        await handleFlutterwavePayment(created);
      } else {
        throw new Error('Unsupported payment method');
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      console.error('Payment error response:', error.response);

      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again and try again.');
        // Clear invalid token (handled by backend session)
        // Redirect to login
        window.location.href = `${getCustomerBaseUrl()}/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}&store=${storeSlug}`;
      } else if (error.response?.status === 400 && error.response?.data?.error?.includes('not enabled')) {
        // User-friendly message for disabled payment gateways
        setError('💳 This store hasn\'t enabled any payment methods yet. Please contact the store owner to complete your purchase.');
      } else {
        setError(error.response?.data?.message || error.response?.data?.error || error.message || 'Payment failed');
      }
      setLoading(false);
    }
  };

  const handlePaystackPayment = async (order) => {
    // Use order.total from backend response (most accurate, includes discount)
    // Fall back to finalTotal which includes discount
    const paymentAmount = order?.total || finalTotal;

    console.log('[PAYSTACK] Payment initialization:');
    console.log('[PAYSTACK] - order object:', order);
    console.log('[PAYSTACK] - order.total:', order?.total);
    console.log('[PAYSTACK] - finalTotal:', finalTotal);
    console.log('[PAYSTACK] - calculatedTotal:', calculatedTotal);
    console.log('[PAYSTACK] - appliedDiscount:', appliedDiscount);
    console.log('[PAYSTACK] - Using paymentAmount:', paymentAmount);

    if (!window.PaystackPop) {
      throw new Error('Paystack script not loaded. Please refresh the page and try again.');
    }

    const paystackKey = sanitizeGatewayKey(paymentCredentials.paystack?.publicKey);
    if (!paystackKey) {
      throw new Error('Paystack public key not configured. Please contact support.');
    }

    const amountInKobo = Math.round(paymentAmount * 100);
    console.log('[PAYSTACK] - Amount in kobo:', amountInKobo);

    const handler = window.PaystackPop.setup({
      key: paystackKey,
      email: customerInfo.email,
      amount: amountInKobo, // Convert to smallest unit (kobo for NGN, pesewas for GHS, etc.)
      currency: storeCurrency || 'NGN',
      metadata: {
        orderId: order.id || order._id,
        storeSlug: storeSlug
      },
      callback: function(response) {
        console.log('Paystack callback response:', response);
        handlePaymentVerification(response.reference, order.id || order._id);
      },
      onClose: function() {
        console.log('Paystack popup closed');
        setLoading(false);
      }
    });

    handler.open();
  };

  // Handle Flutterwave inline checkout with backend-provided payment data
  const handleFlutterwaveInlineCheckout = async (payment, order) => {
    if (!window.FlutterwaveCheckout) {
      throw new Error('Flutterwave script not loaded. Please refresh the page and try again.');
    }

    const publicKey = sanitizeGatewayKey(payment.publicKey);
    if (!publicKey) {
      throw new Error('Flutterwave public key not provided. Please contact support.');
    }

    const paymentData = payment.paymentData;
    console.log('[FLUTTERWAVE INLINE] Initializing with:', paymentData);
    console.log('[FLUTTERWAVE INLINE] Redirect URL for verification:', paymentData.redirect_url);

    // NOTE: For inline checkout (popup), we DON'T set redirect_url
    // because Flutterwave would redirect automatically and skip our callback.
    // Instead, we handle the redirect ourselves in the callback.
    window.FlutterwaveCheckout({
      public_key: publicKey,
      tx_ref: paymentData.tx_ref,
      amount: paymentData.amount,
      currency: paymentData.currency || 'NGN',
      // redirect_url is intentionally NOT set for inline checkout
      // We handle redirect in callback to include transaction_id
      payment_options: paymentData.payment_options || 'card,banktransfer,ussd',
      customer: paymentData.customer,
      customizations: paymentData.customizations,
      // CRITICAL: Pass meta with orderId for verification
      meta: paymentData.meta,
      callback: (response) => {
        console.log('[FLUTTERWAVE INLINE] Payment callback received:', response);
        console.log('[FLUTTERWAVE INLINE] Transaction ID:', response.transaction_id);
        console.log('[FLUTTERWAVE INLINE] Status:', response.status);

        if (response.status === 'successful' || response.status === 'completed') {
          // Build verification URL with transaction_id for Flutterwave verification
          const verifyUrl = `${paymentData.redirect_url}&transaction_id=${response.transaction_id}&status=${response.status}`;
          console.log('[FLUTTERWAVE INLINE] Redirecting to:', verifyUrl);
          window.location.href = verifyUrl;
        } else {
          console.error('[FLUTTERWAVE INLINE] Payment not successful:', response.status);
          setError('Payment was not successful. Please try again.');
          setLoading(false);
        }
      },
      onclose: () => {
        console.log('[FLUTTERWAVE INLINE] Modal closed by user');
        setLoading(false);
      }
    });
  };

  const handleFlutterwavePayment = async (order) => {
    // Use order.total if available (includes discount), otherwise finalTotal
    const paymentAmount = order?.total || finalTotal;

    if (!window.FlutterwaveCheckout) {
      throw new Error('Flutterwave script not loaded. Please refresh the page and try again.');
    }

    const flutterwaveKey = sanitizeGatewayKey(paymentCredentials.flutterwave?.publicKey);
    if (!flutterwaveKey) {
      throw new Error('Flutterwave public key not configured. Please contact support.');
    }

    const transactionRef = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    window.FlutterwaveCheckout({
      public_key: flutterwaveKey,
      tx_ref: transactionRef,
      amount: paymentAmount,
      currency: storeCurrency || 'NGN',
      payment_options: 'card,mobilemoney,ussd',
      customer: {
        email: customerInfo.email,
        name: `${customerInfo.firstName} ${customerInfo.lastName}`,
        phone_number: customerInfo.phone
      },
      customizations: {
        title: 'Revstar Payment',
        description: `Payment for order ${order.id || order._id}`,
        logo: 'https://revstar.com/logo.png'
      },
      callback: (response) => {
        console.log('Flutterwave callback received:', response);
        console.log('Transaction ID from callback:', response.transaction_id);
        console.log('Transaction reference from callback:', response.tx_ref);
        // Use tx_ref as the reference for verification
        handlePaymentVerification(response.tx_ref, order.id || order._id);
      },
      onclose: () => {
        setLoading(false);
      }
    });
  };

  // Show loading state while checking authentication
  if (checkingAuth || loadingStoreSettings) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #111827',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <Card style={{ textAlign: 'center', padding: '48px' }}>
          <h2 style={{ marginBottom: '16px', color: '#111827' }}>Your cart is empty</h2>
          <p style={{ marginBottom: '24px', color: '#6b7280' }}>
            Add some products to your cart before checking out.
          </p>
          <Button
            onClick={() => navigate(`/s/${storeSlug}`)}
            variant="primary"
          >
            Continue Shopping
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '24px 0' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Mobile Optimizations for Checkout */
          @media (max-width: 768px) {
            .checkout-main-grid {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
            }

            .checkout-page-container {
              padding: 0 16px !important;
            }

            .checkout-header-title {
              font-size: 28px !important;
            }

            .checkout-form-grid-2col {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }

            .checkout-form-grid-3col {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }

            .checkout-input {
              min-height: 48px !important;
            }

            .checkout-card {
              padding: 20px 16px !important;
            }

            .checkout-payment-grid {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }

            .checkout-payment-option {
              min-height: 60px !important;
              padding: 16px !important;
            }

            .place-order-button {
              width: 100% !important;
              min-height: 48px !important;
              font-size: 16px !important;
            }

            .order-summary-card {
              position: static !important;
              margin-bottom: 24px !important;
            }

            .checkout-coupon-input-row {
              flex-direction: column !important;
              gap: 12px !important;
            }

            .checkout-coupon-input-row > input {
              flex: 1 1 100% !important;
            }

            .checkout-coupon-input-row > button {
              width: 100% !important;
            }
          }

          @media (max-width: 480px) {
            .checkout-page-container {
              padding: 0 12px !important;
            }

            .checkout-header-title {
              font-size: 24px !important;
            }

            .checkout-card {
              padding: 16px 12px !important;
            }

            .checkout-form-grid-2col,
            .checkout-form-grid-3col {
              gap: 12px !important;
            }
          }
        `}
      </style>
      <div className="checkout-page-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 className="checkout-header-title" style={{ fontSize: '32px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            Checkout
          </h1>
          <p style={{ color: '#6b7280' }}>
            Complete your order securely
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#dc2626'
          }}>
            {error}
          </div>
        )}

        <div className="checkout-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '32px' }}>
          {/* Checkout Form */}
          <div>
            {/* Welcome Message for Logged-in Customers */}
            {isCustomerLoggedIn && customer && (
              <Card className="checkout-card" style={{ marginBottom: '24px', backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: '#0ea5e9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    {customer.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0', color: '#0c4a6e' }}>
                      Welcome back, {customer.firstName}!
                    </h3>
                    <p style={{ fontSize: '14px', margin: '0', color: '#0369a1' }}>
                      Your billing information has been pre-filled from your account.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Customer Information */}
            <Card className="checkout-card" style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#111827' }}>
                Customer Information
              </h3>
              
              <div className="checkout-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <Input
                  label="First Name *"
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, firstName: e.target.value }))}
                  disabled={customerAuthLoading || isCustomerLoggedIn}
                  required
                  className="checkout-input"
                  style={{
                    backgroundColor: isCustomerLoggedIn ? '#f3f4f6' : 'white',
                    cursor: isCustomerLoggedIn ? 'not-allowed' : 'text'
                  }}
                />
                <Input
                  label="Last Name *"
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, lastName: e.target.value }))}
                  disabled={customerAuthLoading || isCustomerLoggedIn}
                  required
                  className="checkout-input"
                  style={{
                    backgroundColor: isCustomerLoggedIn ? '#f3f4f6' : 'white',
                    cursor: isCustomerLoggedIn ? 'not-allowed' : 'text'
                  }}
                />
              </div>
              
              <div className="checkout-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <Input
                    label="Email *"
                    type="email"
                    value={customerInfo.email}
                    onChange={handleEmailChange}
                    disabled={customerAuthLoading || isCustomerLoggedIn}
                    required
                    readOnly={isCustomerLoggedIn}
                    className="checkout-input"
                    style={{
                      backgroundColor: (customerAuthLoading || isCustomerLoggedIn) ? '#f3f4f6' : 'white',
                      cursor: (customerAuthLoading || isCustomerLoggedIn) ? 'not-allowed' : 'text',
                      opacity: (customerAuthLoading || isCustomerLoggedIn) ? 0.7 : 1
                    }}
                  />
                  {isCustomerLoggedIn && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      🔒 Locked to your account email
                    </p>
                  )}
                  {customerAuthLoading && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      ⏳ Loading your account...
                    </p>
                  )}
                </div>
                <Input
                  label="Phone *"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                  required
                  className="checkout-input"
                />
              </div>
              
              <Input
                label="Address"
                value={customerInfo.address}
                onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                className="checkout-input"
                style={{ marginBottom: '16px' }}
              />

              <div className="checkout-form-grid-3col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <Input
                  label="City"
                  value={customerInfo.city}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, city: e.target.value }))}
                  className="checkout-input"
                />
                <Input
                  label="State"
                  value={customerInfo.state}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, state: e.target.value }))}
                  className="checkout-input"
                />
                <Input
                  label="ZIP Code"
                  value={customerInfo.zipCode}
                  onChange={(e) => setCustomerInfo(prev => ({ ...prev, zipCode: e.target.value }))}
                  className="checkout-input"
                />
              </div>
            </Card>

            {/* Shipping Information */}
            {hasPhysicalProducts && (
              <Card className="checkout-card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    Shipping Information
                  </h3>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={useBillingForShipping}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setUseBillingForShipping(isChecked);
                        if (isChecked) {
                          // Copy billing info to shipping info
                          setShippingInfo({
                            ...customerInfo,
                            // Ensure we trigger a state change for shipping calculator
                            __timestamp: Date.now()
                          });
                        }
                      }}
                    />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Same as billing address</span>
                  </label>
                </div>
                
                {!useBillingForShipping && (
                  <>
                    <div className="checkout-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <Input
                        label="First Name *"
                        value={shippingInfo.firstName}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                        className="checkout-input"
                      />
                      <Input
                        label="Last Name *"
                        value={shippingInfo.lastName}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, lastName: e.target.value }))}
                        required
                        className="checkout-input"
                      />
                    </div>

                    <Input
                      label="Address *"
                      value={shippingInfo.address}
                      onChange={(e) => setShippingInfo(prev => ({ ...prev, address: e.target.value }))}
                      style={{ marginBottom: '16px' }}
                      required
                      className="checkout-input"
                    />

                    <div className="checkout-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <Input
                        label="City *"
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, city: e.target.value }))}
                        required
                        className="checkout-input"
                      />
                      <Input
                        label="State *"
                        value={shippingInfo.state}
                        onChange={(e) => setShippingInfo(prev => ({ ...prev, state: e.target.value }))}
                        required
                        className="checkout-input"
                      />
                    </div>
                  </>
                )}
                
                {/* Shipping Calculator */}
                <div>
                  <ShippingCalculator
                    key={`shipping-${useBillingForShipping ? 'billing' : 'separate'}-${currentState}`}
                    storeSlug={storeSlug}
                    state={currentState}
                    city={useBillingForShipping ? customerInfo.city : shippingInfo.city}
                    address={useBillingForShipping ? customerInfo.address : shippingInfo.address}
                    postalCode={useBillingForShipping ? customerInfo.zipCode : shippingInfo.zipCode}
                    subtotal={total}
                    onShippingCalculated={handleShippingCalculated}
                  />

                  {/* Chowdeck Delivery Option */}
                  {currentState && shippingCalculation && (
                    <DeliveryMethodSelector
                      storeSlug={storeSlug}
                      deliveryState={currentState}
                      deliveryAddress={useBillingForShipping ? customerInfo.address : shippingInfo.address}
                      deliveryCity={useBillingForShipping ? customerInfo.city : shippingInfo.city}
                      standardShippingCost={getEffectiveShippingFee(shippingCalculation, 'STATIC')}
                      standardShippingMethod={shippingCalculation?.method}
                      standardEstimate={shippingCalculation?.deliveryEstimate}
                      currency={{ format: (amt) => `₦${amt?.toLocaleString() || 0}` }}
                      onDeliveryMethodChange={handleDeliveryMethodChange}
                      disabled={loading}
                    />
                  )}
                </div>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div>

            <Card className="order-summary-card checkout-card">
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#111827' }}>
                Order Summary
              </h3>
              
              <div style={{ marginBottom: '20px' }}>
                {items.map((item) => (
                  <div key={item.product._id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: '1px solid #f3f4f6'
                  }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>
                        {item.product.name}
                      </p>
                      <p style={{ fontSize: '14px', color: '#6b7280' }}>
                        Qty: {item.qty}
                      </p>
                    </div>
                    <p style={{ fontWeight: '600', color: '#111827' }}>
                      {currency.format(item.product.price * item.qty)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Coupon Code Section */}
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
                  Have a coupon code?
                </h4>

                {appliedDiscount ? (
                  // Show applied discount
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '8px',
                    border: '1px solid #059669'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg style={{ width: '20px', height: '20px', color: '#059669' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#065f46' }}>
                          {appliedDiscount.code}
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#047857' }}>
                          Discount applied!
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #059669',
                        borderRadius: '6px',
                        color: '#059669',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  // Show coupon input form
                  <div>
                    <div className="checkout-coupon-input-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                        placeholder="Enter coupon code"
                        disabled={couponLoading}
                        className="checkout-input"
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: `1px solid ${couponError ? '#dc2626' : '#d1d5db'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          backgroundColor: couponLoading ? '#f3f4f6' : 'white',
                          textTransform: 'uppercase'
                        }}
                      />
                      <button
                        data-apply-coupon
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: couponLoading || !couponCode.trim() ? '#9ca3af' : '#111827',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: couponLoading || !couponCode.trim() ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap',
                          minHeight: '44px'
                        }}
                      >
                        {couponLoading ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                    {couponError && (
                      <p style={{ margin: 0, fontSize: '12px', color: '#dc2626' }}>
                        {couponError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <CustomerFacingSubtotal
                  subtotal={total}
                  processingFee={
                    paymentMethod !== 'cod'
                      && serviceChargeInfo
                      && String(serviceChargeInfo.whoPays || '').toLowerCase() === 'customer'
                      ? serviceChargeInfo.serviceCharge
                      : 0
                  }
                  processingFeeLabel={platformFeeQuote ? 'Subtotal incl. transaction fee' : undefined}
                  feeBreakdown={platformFeeQuote ? [{ label: 'Transaction fee', amount: serviceChargeInfo.serviceCharge }] : null}
                  formatCurrency={currency.format}
                  itemCount={items.length}
                  labelStyle={{ color: '#374151', fontWeight: 500 }}
                  valueStyle={{ fontWeight: 600 }}
                />

              {/* Tax (if enabled by merchant) */}
              {quoteTax.enabled && quoteTax.amount > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>
                      {quoteTax.name || 'Tax'} ({quoteTax.rate}%{quoteTax.inclusive ? ' incl.' : ''})
                    </span>
                    <span
                      title="Set by the store owner"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#e5e7eb',
                        color: '#6b7280',
                        fontSize: '10px',
                        fontWeight: '600',
                        cursor: 'help'
                      }}
                    >
                      i
                    </span>
                  </div>
                  <span style={{ color: '#111827', fontSize: '16px', fontWeight: '600' }}>
                    {currency.format(quoteTax.amount)}
                  </span>
                </div>
              )}

              {/* Discount */}
              {appliedDiscount && appliedDiscount.discountAmount > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: '#d1fae5',
                  margin: '0 -16px',
                  paddingLeft: '16px',
                  paddingRight: '16px'
                }}>
                  <div>
                    <span style={{ color: '#047857', fontSize: '14px', fontWeight: '600' }}>
                      Discount ({appliedDiscount.code})
                    </span>
                    {appliedDiscount.details && (
                      <p style={{ color: '#059669', fontSize: '12px', margin: '2px 0 0 0' }}>
                        {appliedDiscount.details.name}
                      </p>
                    )}
                  </div>
                  <span style={{ color: '#047857', fontSize: '16px', fontWeight: '700' }}>
                    -{currency.format(appliedDiscount.discountAmount)}
                  </span>
                </div>
              )}

              {/* Shipping */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <div>
                  <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>
                    {deliveryMethod === 'CHOWDECK' ? (
                      <>Shipping <span style={{ color: '#f97316', fontWeight: '600' }}>(Chowdeck Express)</span></>
                    ) : (
                      <>Shipping {shippingCalculation?.method ? `(${shippingCalculation.method})` : ''}</>
                    )}
                  </span>
                  <p style={{ color: '#6b7280', fontSize: '12px', margin: '2px 0 0 0' }}>
                    {deliveryMethod === 'CHOWDECK' ? '30-60 mins • Same-day' : (shippingCalculation?.estimatedDays || 'Calculating...')}
                  </p>
                </div>
                <span style={{
                  color: deliveryMethod === 'CHOWDECK' ? '#f97316' : (shippingCalculation ? '#111827' : '#6b7280'),
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  {deliveryMethod === 'CHOWDECK' ? (
                    currency.format(chowdeckDeliveryFee || 0)
                  ) : shippingCalculation ? (
                    currency.format(getEffectiveShippingFee(shippingCalculation, 'STATIC'))
                  ) : (
                    'TBD'
                  )}
                </span>
              </div>
                
                
                {/* Total */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '16px 0 0 0',
                  marginTop: '12px',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <span style={{ 
                    color: '#111827', 
                    fontSize: '18px', 
                    fontWeight: '700' 
                  }}>
                    Total
                  </span>
                  <span style={{ 
                    color: '#111827', 
                    fontSize: '20px', 
                    fontWeight: '700' 
                  }}>
                    {currency.format(finalTotal)}
                  </span>
                </div>
              </div>

              {/* Currency Notice - shown when display currency differs from store currency */}
              {displayCurrency && storeCurrency && displayCurrency !== storeCurrency && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '18px' }}>💱</span>
                  <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                    <strong>Currency Notice:</strong> You will be charged in <strong>{storeCurrency}</strong>.
                    {' '}The {displayCurrency} prices shown are estimates based on current exchange rates.
                    {' '}Your bank may apply additional conversion fees for international cards.
                  </div>
                </div>
              )}

              {/* Payment Method Selection */}
              {console.log('[CHECKOUT RENDER] paymentGateways:', checkoutPaymentMethods, 'length:', checkoutPaymentMethods.length)}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 16px 0'
                }}>
                  How would you like to pay?
                </h3>

                {loadingGateways ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    color: '#6b7280'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid #d1d5db',
                      borderTop: '2px solid #111827',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></div>
                    Loading payment options...
                  </div>
                ) : checkoutPaymentMethods.length === 0 ? (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    color: '#92400e',
                    fontSize: '14px'
                  }}>
                    No payment methods available. Please contact the store owner.
                  </div>
                ) : checkoutPaymentMethods.length === 1 ? (
                  // Single gateway - show it selected automatically
                  <div style={{
                    padding: '16px',
                    backgroundColor: checkoutPaymentMethods[0].color || '#00D4AA',
                    borderRadius: '12px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>{checkoutPaymentMethods[0].iconElement || <span style={{ fontSize: '1.2rem' }}>{checkoutPaymentMethods[0].icon || '💳'}</span>}</span>
                    <span style={{ fontWeight: '600' }}>{checkoutPaymentMethods[0].name}</span>
                  </div>
                ) : (
                  // Multiple gateways - show selection grid
                  <div className="checkout-payment-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(checkoutPaymentMethods.length, 2)}, 1fr)`,
                    gap: '12px',
                    maxWidth: '600px'
                  }}>
                    {checkoutPaymentMethods.map((gateway) => (
                      <label
                        key={gateway.id}
                        className="checkout-payment-option"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px',
                          border: paymentMethod === gateway.id ? `2px solid ${gateway.color || '#00D4AA'}` : '2px solid #d1d5db',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          background: paymentMethod === gateway.id ? (gateway.color || '#00D4AA') : 'white',
                          color: paymentMethod === gateway.id ? 'white' : '#374151',
                          justifyContent: 'flex-start',
                          transition: 'all 0.2s ease',
                          minHeight: '60px',
                          position: 'relative'
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={gateway.id}
                          checked={paymentMethod === gateway.id}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            pointerEvents: 'none'
                          }}
                        />
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          textAlign: 'center'
                        }}>
                          <div style={{
                            fontSize: '1.2rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px'
                          }}>
                            {gateway.iconElement || gateway.icon || '💳'}
                          </div>
                          <div style={{ textAlign: 'left', minWidth: 0 }}>
                            <div style={{
                              fontWeight: '600',
                              fontSize: '14px',
                              whiteSpace: 'nowrap'
                            }}>
                              {gateway.name}
                            </div>
                            {gateway.description && (
                              <div style={{ fontSize: '11px', lineHeight: 1.35, opacity: 0.82, marginTop: '2px' }}>
                                {gateway.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>


              <Button
                onClick={handlePayment}
                disabled={loading || shippingLoading || (hasPhysicalProducts && !shippingCalculation)}
                variant="primary"
                className="place-order-button"
                style={{
                  width: '100%',
                  marginBottom: '12px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '600',
                  borderRadius: '8px',
                  background: (loading || shippingLoading || (hasPhysicalProducts && !shippingCalculation)) ? '#9ca3af' : '#111827',
                  border: 'none',
                  cursor: (loading || shippingLoading || (hasPhysicalProducts && !shippingCalculation)) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Processing...
                  </div>
                ) : shippingLoading || (hasPhysicalProducts && !shippingCalculation) ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Calculating shipping...
                  </div>
                ) : checkoutPaymentMethods.length > 1 ? (
                  paymentMethod === 'cod'
                    ? `Place COD order — ${currency.format(finalTotal)} due on delivery`
                    : paymentMethod === 'BANK_TRANSFER'
                      ? `Continue to bank transfer — ${currency.format(finalTotal)}`
                    : `Pay with ${checkoutPaymentMethods.find(g => g.id === paymentMethod)?.name || paymentMethod} - ${currency.format(finalTotal)}`
                ) : (
                  paymentMethod === 'cod'
                    ? `Place COD order — ${currency.format(finalTotal)} due on delivery`
                    : paymentMethod === 'BANK_TRANSFER'
                      ? `Continue to bank transfer — ${currency.format(finalTotal)}`
                      : `Pay ${currency.format(finalTotal)}`
                )}
              </Button>
              
              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                {paymentMethod === 'cod'
                  ? 'The merchant will confirm your order before delivery. No payment is taken now.'
                  : paymentMethod === 'BANK_TRANSFER'
                    ? 'You will receive the merchant’s transfer details on the next screen.'
                    : `Secure payment powered by ${paymentMethod === 'flutterwave' ? 'Flutterwave' : 'Paystack'}`}
              </p>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Email Detection Modal */}
      {showEmailDetection && (
        <EmailDetection
          email={detectedEmail}
          onEmailExists={handleEmailExists}
          onEmailNew={handleEmailNew}
          onCancel={handleEmailDetectionCancel}
          canCancel={!storeSettings?.requireAccountRegistration}
          requireRegistration={storeSettings?.requireAccountRegistration}
        />
      )}
    </div>
  );
}

export default function Checkout() {
  return <CheckoutContent />;
}
